import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

const PEGS = [0, 1, 2];
const COLORS = ["#FF003C", "#EFFF04", "#39FF14"];

export function HanoiGame({ onScore }) {
  const [towers, setTowers] = useState([[3, 2, 1], [], []]);
  const [sel, setSel] = useState(-1);
  const [moves, setMoves] = useState(0);
  const [running, setRunning] = useState(false);
  const [won, setWon] = useState(false);
  const reported = useRef(false);

  const start = () => { setTowers([[3, 2, 1], [], []]); setSel(-1); setMoves(0); setRunning(true); setWon(false); reported.current = false; };

  const tap = (p) => {
    if (!running) return;
    if (sel === -1) { if (towers[p].length) setSel(p); return; }
    if (sel === p) { setSel(-1); return; }
    const from = towers[sel], to = towers[p];
    const disk = from[from.length - 1];
    if (!to.length || to[to.length - 1] > disk) {
      const nt = towers.map((t) => [...t]);
      nt[p].push(nt[sel].pop());
      setTowers(nt); setMoves((m) => m + 1); setSel(-1);
      if (nt[2].length === 3) { setRunning(false); setWon(true); }
    } else setSel(-1);
  };

  useEffect(() => { if (won && !reported.current) { reported.current = true; onScore?.(Math.max(100, 800 - moves * 50)); } }, [won, moves, onScore]);

  return (
    <div className="flex flex-col items-center" data-testid="hanoi-game">
      <p className="font-mono text-neon-cyan text-lg mb-1" data-testid="hanoi-moves">MOVES: {moves}</p>
      <p className="text-zinc-500 text-xs font-mono mb-5">Best possible: 7 moves</p>
      <div className="relative flex gap-3 items-end justify-center bg-zinc-900 border-2 border-zinc-700 rounded-xl p-4" style={{ width: "min(92vw, 380px)", height: 220 }}>
        {PEGS.map((p) => (
          <button key={p} onClick={() => tap(p)} data-testid={`hanoi-peg-${p}`} className="relative flex-1 h-full flex flex-col justify-end items-center">
            <div className="absolute bottom-0 top-3 w-1.5 bg-zinc-600 rounded" />
            <div className="absolute bottom-0 w-full h-1.5 bg-zinc-600" />
            {towers[p].map((d, i) => (
              <div key={i} className="rounded-md mb-1 z-10" style={{ width: `${30 + d * 22}%`, height: 22, background: COLORS[d - 1], boxShadow: sel === p && i === towers[p].length - 1 ? "0 0 12px #fff" : "none" }} />
            ))}
          </button>
        ))}
        {!running && (
          <div className="absolute inset-0 bg-black/85 rounded-xl flex flex-col items-center justify-center gap-3">
            {won && <p className="font-display font-black text-2xl text-neon-lime uppercase" data-testid="hanoi-won">Solved in {moves}!</p>}
            <Button onClick={start} data-testid="hanoi-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
              <Play className="w-4 h-4 mr-1 fill-black" /> {won ? "Play Again" : "Start"}
            </Button>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-3">Move all disks to the right peg. Never put a big disk on a smaller one!</p>
    </div>
  );
}
